// src/pages/ProductPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';

import ProductDrawer from '../components/ProductDrawer';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { deriveFromSale, formatPercent, resolveTaxRateValue } from '../utils/pricing';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';

const RECENT_IMPORT_WINDOW_MS = 1000 * 60 * 60 * 24; // 24 hours

export default function ProductPage() {
  const currencyCode = useCurrencyCode();
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [wooStatus, setWooStatus] = useState(null);
  const [syncingWoo, setSyncingWoo] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products', { params: { includeInactive: true } });
      setProducts(data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api
      .get('/categories')
      .then((r) => setCategories(r.data))
      .catch((err) => console.error(err));

    api
      .get('/taxrates')
      .then((r) => setTaxRates(r.data))
      .catch((err) => console.error(err));

    fetchProducts();
  }, []);

  const fetchWooStatus = async () => {
    try {
      const { data } = await api.get('/integrations/woocommerce/status');
      setWooStatus(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchWooStatus();
  }, []);

  useEffect(() => {
    if (location.state?.openProductCreate) {
      openCreateDrawer();
      navigate('/products', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const openCreateDrawer = () => {
    setSelectedProduct(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  };

  const openEditDrawer = (product) => {
    setSelectedProduct(product);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedProduct(null);
  };

  const handleDrawerSaved = () => {
    handleDrawerClose();
  };

  const handleWooSync = async () => {
    setSyncingWoo(true);
    try {
      const { data } = await api.post('/integrations/woocommerce/sync');
      const {
        matched = 0,
        totalProducts = 0,
        created = 0,
        unmatched = [],
        skippedNoSku = [],
      } = data;

      const parts = [
        `Matched ${matched}`,
        created ? `Created ${created}` : null,
        unmatched?.length ? `${unmatched.length} unmatched` : null,
        skippedNoSku?.length ? `${skippedNoSku.length} without SKU` : null,
        `from ${totalProducts} Woo products`,
      ].filter(Boolean);

      toast.success(parts.join(' | '));
      await fetchProducts();
      await fetchWooStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setSyncingWoo(false);
    }
  };

  const adoptableTaxRateValue = (product) =>
    resolveTaxRateValue(taxRates, product?.taxRateId);

  const buildUpdatePayload = (product, overrides = {}) => ({
    name: product.name,
    sku: product.sku,
    categoryId: product.categoryId,
    taxRateId: product.taxRateId,
    priceExclTax: product.priceExclTax,
    priceInclTax: product.priceInclTax,
    stock: product.stock,
    barcode: product.barcode,
    costExclTax: product.costExclTax,
    active: product.active !== false,
    ...overrides,
  });

  const toggleProductActive = async (product, nextActive) => {
    try {
      await api.put(`/products/${product.id}`, buildUpdatePayload(product, { active: nextActive }));
      toast.success(nextActive ? 'Product activated' : 'Product deactivated');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };

  const bulkAdoptPrice = async (source) => {
    const eligible = filteredProducts.filter((product) =>
      source === 'regular'
        ? product.woocommerceRegularPrice !== null
        : product.woocommerceSalePrice !== null
    );

    if (eligible.length === 0) {
      toast.info(source === 'regular' ? 'No products with Woo regular price available.' : 'No products with Woo sale price available.');
      return;
    }

    setBulkApplying(source);
    try {
      for (const product of eligible) {
        await api.post(`/products/${product.id}/adopt-price`, { source });
      }
      toast.success(
        `Adopted Woo ${source === 'regular' ? 'regular' : 'sale'} price for ${eligible.length} product${eligible.length === 1 ? '' : 's'}.`
      );
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setBulkApplying(null);
    }
  };

  const enrichedProducts = useMemo(() => {
    return products.map((product) => {
      const derived = deriveFromSale({
        costExclTax: product.costExclTax,
        salePriceInclTax: product.priceInclTax,
        taxRate: adoptableTaxRateValue(product),
      });
      return {
        ...product,
        derivedMargin: derived.marginPercent,
        derivedSaleExcl: derived.salePriceExclTax,
      };
    });
  }, [products, taxRates]);

  const filteredProducts = enrichedProducts.filter((product) => {
    const search = filter.toLowerCase();
    return (
      product.name.toLowerCase().includes(search) ||
      product.sku.toLowerCase().includes(search) ||
      (product.barcode || '').toLowerCase().includes(search)
    );
  });

  const categoryNameLookup = useMemo(() => {
    const lookup = new Map();
    categories.forEach((category) => lookup.set(category.id, category.name));
    return lookup;
  }, [categories]);

  const isRecentlyImported = (product) => {
    if (!product?.woocommerceId || !product?.createdAt) return false;
    const createdMs = new Date(product.createdAt).getTime();
    return Date.now() - createdMs <= RECENT_IMPORT_WINDOW_MS;
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-4 overflow-hidden bg-slate-950 px-4 pb-6 pt-4 text-slate-100">
      <ToastContainer position="top-center" autoClose={2500} theme="dark" />

      {wooStatus && (
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900 px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-100">
              WooCommerce integration: {wooStatus.configured ? 'Configured' : 'Not configured'}
            </p>
            <p className="text-xs text-slate-400">
              {wooStatus.configured
                ? wooStatus.lastSync
                  ? `Last sync: ${new Date(wooStatus.lastSync).toLocaleString()}`
                  : 'No sync has been run yet.'
                : 'Add WooCommerce credentials in settings to enable sync.'}
            </p>
          </div>
          <button
            onClick={handleWooSync}
            disabled={syncingWoo || !wooStatus.configured}
            className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition ${
              wooStatus.configured
                ? syncingWoo
                  ? 'cursor-not-allowed bg-slate-700 text-slate-400'
                  : 'bg-emerald-500 text-white hover:bg-emerald-400'
                : 'cursor-not-allowed bg-slate-800 text-slate-500'
            }`}
          >
            {syncingWoo ? 'Syncing...' : 'Sync WooCommerce'}
          </button>
        </div>
      )}

      <section className="flex flex-1 flex-col overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/70 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
        <header className="sticky top-0 z-10 border-b border-slate-800 bg-slate-950/85 px-5 py-5 backdrop-blur">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold leading-tight text-slate-100">Product catalogue</h2>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {`Showing ${filteredProducts.length} of ${enrichedProducts.length} products`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={openCreateDrawer}
                className="inline-flex items-center justify-center rounded-full bg-brandGreen px-4 py-2 text-sm font-semibold text-white transition hover:bg-brandDark focus:outline-none focus-visible:ring-2 focus-visible:ring-brandGreen/60"
              >
                Add Product
              </button>
              <button
                onClick={() => bulkAdoptPrice('regular')}
                disabled={bulkApplying !== null}
                className="inline-flex items-center justify-center rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {bulkApplying === 'regular' ? 'Applying...' : 'Use Woo regular'}
              </button>
              <button
                onClick={() => bulkAdoptPrice('sale')}
                disabled={bulkApplying !== null}
                className="inline-flex items-center justify-center rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {bulkApplying === 'sale' ? 'Applying...' : 'Use Woo sale'}
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <label className="relative flex w-full items-center lg:max-w-sm">
              <span className="pointer-events-none absolute left-4 top-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Search
              </span>
              <input
                type="text"
                placeholder="Search by name, SKU or barcode"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                className="w-full rounded-2xl border border-slate-800 bg-slate-950/90 px-4 pb-2 pt-6 text-sm text-slate-100 placeholder:text-slate-500 focus:border-brandGreen focus:outline-none focus:ring-2 focus:ring-brandGreen/40"
              />
            </label>
            <p className="text-xs text-slate-500">
              WooCommerce actions update in the background.
            </p>
          </div>
        </header>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Loading...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-400">
              No products found.
            </div>
          ) : (
            <table className="min-w-[1200px] border-separate border-spacing-0 text-sm text-slate-200">
              <thead className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur">
                <tr>
                  {[
                    'Product',
                    'SKU',
                    'Barcode',
                    'Category',
                    'Cost (ex)',
                    'POS price',
                    'Margin',
                    'Woo pricing',
                    'Stock',
                    'Actions',
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="whitespace-nowrap border-b border-slate-800 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const categoryName =
                    categoryNameLookup.get(product.categoryId) || '--';
                  const marginDisplay = formatPercent(product.derivedMargin, '0.00');
                  const localPrice = parseFloat(product.priceInclTax ?? product.price ?? 0);
                  const wooRegular =
                    product.woocommerceRegularPrice !== null
                      ? parseFloat(product.woocommerceRegularPrice)
                      : null;
                  const wooSale =
                    product.woocommerceSalePrice !== null
                      ? parseFloat(product.woocommerceSalePrice)
                      : null;
                  const regularDiff = wooRegular !== null ? wooRegular - localPrice : null;
                  const saleDiff = wooSale !== null ? wooSale - localPrice : null;
                  const formatDelta = (diff) => {
                    if (diff === null || Number.isNaN(diff)) return null;
                    const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
                    const woocommerceCurrency = product.woocommerceCurrency || currencyCode;
                    return `${sign}${formatCurrency(Math.abs(diff), woocommerceCurrency)}`;
                  };
                  const diffTone = (diff) => {
                    if (diff === null || Number.isNaN(diff) || diff === 0) return 'text-slate-500';
                    return diff > 0 ? 'text-amber-400' : 'text-emerald-400';
                  };
                  return (
                    <tr
                      key={product.id}
                      className="border-b border-slate-800 bg-slate-900/60 transition-colors hover:bg-slate-900"
                    >
                      <td className="px-4 py-4 align-top">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-slate-100">
                              {product.name}
                            </span>
                            {product.active === false && (
                              <span className="rounded-full border border-red-700/50 bg-red-900/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-200">
                                Inactive
                              </span>
                            )}
                            {isRecentlyImported(product) && (
                              <span className="rounded-full border border-emerald-700/50 bg-emerald-900/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-200">
                                New
                              </span>
                            )}
                          </div>
                          {product.woocommerceId && (
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">
                              Woo #{product.woocommerceId}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-slate-300">
                        {product.sku || '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-slate-300">
                        {product.barcode || '--'}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-slate-300">
                        {categoryName}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-slate-200">
                        {formatCurrency(parseFloat(product.costExclTax || 0), currencyCode)}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-slate-200">
                        {formatCurrency(parseFloat(product.priceInclTax), currencyCode)}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-slate-200">
                        {Number.isFinite(product.derivedMargin) ? `${marginDisplay}%` : '--'}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-slate-200">
                        {product.woocommerceId ? (
                          <div className="space-y-2">
                            {wooSale !== null && (
                              <div className={`text-xs font-medium ${diffTone(saleDiff)}`}>
                                Sale {formatCurrency(wooSale, product.woocommerceCurrency || currencyCode)}
                                <span className="ml-2 font-normal text-slate-500">
                                  {formatDelta(saleDiff) ?? '--'}
                                </span>
                              </div>
                            )}
                            {wooRegular !== null && (
                              <div className={`text-xs font-medium ${diffTone(regularDiff)}`}>
                                Regular{' '}
                                {formatCurrency(wooRegular, product.woocommerceCurrency || currencyCode)}
                                <span className="ml-2 font-normal text-slate-500">
                                  {formatDelta(regularDiff) ?? '--'}
                                </span>
                              </div>
                            )}
                            {product.woocommerceCategories?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {product.woocommerceCategories.map((cat) => (
                                  <span
                                    key={cat.id}
                                    className="rounded-full border border-slate-700 bg-slate-900/70 px-2 py-0.5 text-[10px] text-slate-300"
                                  >
                                    {cat.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">No Woo link</span>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-slate-200">
                        {product.stock ?? '--'}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => openEditDrawer(product)}
                            className="inline-flex items-center justify-center rounded-lg border border-brandGreen/60 bg-brandGreen/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-brandGreen transition hover:bg-brandGreen/20"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleProductActive(product, product.active === false)}
                            className="inline-flex items-center justify-center rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white"
                          >
                            {product.active === false ? 'Activate' : 'Deactivate'}
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete "${product.name}"?`)) return;
                              try {
                                await api.delete(`/products/${product.id}`);
                                toast.success('Product deleted');
                                fetchProducts();
                              } catch (err) {
                                toast.error(err.response?.data?.error || err.message);
                              }
                            }}
                            className="inline-flex items-center justify-center rounded-lg border border-red-700/60 bg-red-900/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-red-300 transition hover:border-red-500 hover:text-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <ProductDrawer
        show={drawerOpen}
        mode={drawerMode}
        product={selectedProduct}
        categories={categories}
        taxRates={taxRates}
        onClose={handleDrawerClose}
        onSaved={handleDrawerSaved}
        onRefresh={fetchProducts}
      />
    </div>
  );
}

